import { ArrowLeftRight, Calendar } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ColumnConfig,
  ColumnFilters,
} from "@/components/MaximusTableReusable";
import {
  defineMultiView,
  type MultiViewDefinition,
  MultiViewTable,
  type MultiViewTableHandle,
} from "@/components/tables/MultiViewTable";
import type { SortInput } from "@/components/tables/tableFilters";
import type { ScreenName } from "@/constants/screenNames";
import type { DefaultColumn } from "@/hooks/useScreenColumnPreferences";
import { useTerminology } from "@/hooks/useTerminology";
import type { ExportColumn } from "@/lib/exportColumns";
import { cn, formatDate, formatDateOnlyLocal } from "@/lib/utils";
import { useFormatting } from "../../hooks/useFormatting";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

export type { SortInput };

export type ViewMode = "balances" | "transactions";

const TRANSACTIONS_VIEW: ViewMode = "transactions";
const BALANCES_VIEW: ViewMode = "balances";

/** Identifier for each Transactions quick-filter button. */
export type QuickFilterKey = "7" | "30" | "90";

/** Inclusive `forecastDate` window (yyyy-MM-dd) for a single quick filter. */
export interface QuickFilterRange {
  ge: string;
  le: string;
}

/** The 7/30/90-day windows built from the forecast start date. */
export type QuickFilterRanges = Record<QuickFilterKey, QuickFilterRange>;

/** Ordered keys / labels for rendering the quick-filter buttons. */
const QUICK_FILTER_KEYS: QuickFilterKey[] = ["7", "30", "90"];
const QUICK_FILTER_LABELS: Record<QuickFilterKey, string> = {
  "7": "7d",
  "30": "30d",
  "90": "90d",
};
const DEFAULT_QUICK_FILTER: QuickFilterKey = "90";

/**
 * Parse a `yyyy-MM-dd` string into a Date at local midnight. Using the local
 * constructor (rather than `new Date(iso)`, which parses as UTC) keeps the
 * round-trip through `formatDateOnlyLocal` stable regardless of timezone, so
 * the visible filter and the derived API filter agree exactly.
 */
function parseIsoDateLocal(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Build the `forecastDate` date-range column filter for a quick-filter range. */
function quickFilterColumnFilters(range: QuickFilterRange): ColumnFilters {
  return {
    forecastDate: {
      dateFrom: parseIsoDateLocal(range.ge),
      dateTo: parseIsoDateLocal(range.le),
    },
  };
}

/** True when the applied `forecastDate` filter matches the given range. */
function forecastDateMatchesRange(
  filters: ColumnFilters,
  range: QuickFilterRange,
): boolean {
  const forecastDate = filters.forecastDate;
  if (!forecastDate?.dateFrom || !forecastDate?.dateTo) return false;
  return (
    formatDateOnlyLocal(forecastDate.dateFrom) === range.ge &&
    formatDateOnlyLocal(forecastDate.dateTo) === range.le
  );
}

/** Unified display row covering both view modes. */
export interface ForecastRow {
  rowKey: string;
  id: number;
  // transactions
  forecastDate?: string | null;
  dueDate?: string | null;
  counterpartyName?: string | null;
  description?: string | null;
  category?: string | null;
  direction?: string | null;
  type?: string | null;
  grossAmount?: number | null;
  outstandingAmount?: number | null;
  layer?: string | null;
  status?: string | null;
  reference?: string | null;
  dataSourceName?: string | null;
  // balances
  date?: string | null;
  openingBalance?: number | null;
  balance?: number | null;
  incoming?: number | null;
  outgoing?: number | null;
  confidenceScore?: number | null;
  contributingLayers?: string | null;
}

/** Structural subset of a transaction node returned by either backing view. */
export interface TransactionNode {
  id: number;
  forecastDate: string | null;
  dueDate: string | null;
  counterpartyName: string | null;
  description: string | null;
  category: string | null;
  direction: string | null;
  type: string | null;
  grossAmount: number | null;
  outstandingAmount: number | null;
  layer: string | null;
  status: string | null;
  reference: string | null;
  dataSourceName: string | null;
}

/** Structural subset of a data-point (balance) node returned by either view. */
export interface BalanceNode {
  id: number;
  date: string | null;
  openingBalance: number | null;
  balance: number | null;
  incoming: number | null;
  outgoing: number | null;
  confidenceScore: number | null;
  contributingLayers: string | null;
}

interface Connection<T> {
  edges: { cursor: string; node: T }[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
}

export interface TransactionOptions {
  category: string[];
  direction: string[];
  type: string[];
  layer: string[];
  status: string[];
  dataSource: string[];
}

/**
 * A data source for {@link TransactionsBalancesTable}. Everything that differs
 * between the base 90-day forecast table and the scenario table is provided
 * here, so the table body itself stays identical across both.
 */
export interface TransactionsTableSource {
  title: string;
  screenName: ScreenName;
  /** Export form names passed to useExportToExcel (live mode only). */
  exportTransactionsForm: string;
  exportBalancesForm: string;
  exportTransactionsFileName: string;
  exportBalancesFileName: string;
  fetchTransactions: (
    filter: Record<string, unknown> | undefined,
    first: number,
    after: string | undefined,
    sort: SortInput[] | undefined,
  ) => Promise<Connection<TransactionNode>>;
  fetchTransactionsCount: (
    filter: Record<string, unknown> | undefined,
  ) => Promise<number>;
  fetchDataPoints: (
    filter: Record<string, unknown> | undefined,
    first: number,
    after: string | undefined,
    sort: SortInput[] | undefined,
  ) => Promise<Connection<BalanceNode>>;
  fetchDataPointsCount: (
    filter: Record<string, unknown> | undefined,
  ) => Promise<number>;
  fetchDistinctValues: (field: string) => Promise<string[]>;
  getDemoTransactionRows: () => ForecastRow[];
  getDemoBalanceRows: () => ForecastRow[];
  getDemoTransactionOptions: () => TransactionOptions;
  /**
   * Horizon constraint merged into every live query for the given view mode.
   * Return an empty object to apply no horizon constraint.
   */
  horizonFilter: (mode: ViewMode) => Record<string, unknown>;
  /**
   * A stable string key describing the current horizon anchor, used only as an
   * effect dependency so live queries refetch when the anchor changes.
   */
  horizonKey: string | null;
}

/**
 * ContributingLayers is stored as a JSON array of CffLayer enum names, e.g.
 * `["Known","CommittedOrders"]`. Render it as a human-readable, comma-separated
 * list with PascalCase names split into words (→ "Known, Committed Orders").
 */
function formatContributingLayers(raw: string | null | undefined): string {
  if (!raw) return "—";
  let names: string[];
  try {
    const parsed: unknown = JSON.parse(raw);
    names = Array.isArray(parsed)
      ? parsed.map((v) => String(v))
      : [String(raw)];
  } catch {
    names = raw
      .replace(/[[\]"]/g, "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const labels = names
    .map((name) => name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim())
    .filter(Boolean);
  return labels.length > 0 ? labels.join(", ") : "—";
}

/**
 * Convert a raw DB value into a human-readable label: split on underscores and
 * whitespace, then capitalise each word (`credit_note` → "Credit Note",
 * `outstanding` → "Outstanding"). Null/empty values fall back to the em-dash.
 */
function formatLabel(raw: string | null | undefined): string {
  if (!raw) return "—";
  const words = raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  return words.length > 0 ? words.join(" ") : "—";
}

/** Map a status value to a Badge variant, matched case-insensitively. */
function statusBadgeVariant(
  status: string,
): "success" | "warning" | "destructive" | "secondary" | "info" {
  switch (status.toLowerCase()) {
    case "settled":
      return "success";
    case "outstanding":
      return "warning";
    case "overdue":
      return "destructive";
    case "unknown":
      return "secondary";
    default:
      return "info";
  }
}

/**
 * Default sort for the transactions view: Forecast Date ascending. Applied on
 * first load (live mode, server-side) and re-applied when switching back to the
 * transactions view. The balances view has no default sort.
 */
export const TRANSACTION_DEFAULT_SORT: SortInput[] = [
  { field: "ForecastDate", order: "Ascending" },
];

const TRANSACTION_SORT_FIELD_MAP: Record<string, string> = {
  forecastDate: "ForecastDate",
  dueDate: "DueDate",
  counterpartyName: "CounterpartyName",
  description: "Description",
  category: "Category",
  direction: "Direction",
  type: "Type",
  grossAmount: "GrossAmount",
  outstandingAmount: "OutstandingAmount",
  layer: "Layer",
  status: "Status",
  reference: "Reference",
  dataSourceName: "DataSourceName",
};

const BALANCE_SORT_FIELD_MAP: Record<string, string> = {
  date: "Date",
  openingBalance: "OpeningBalance",
  balance: "Balance",
  incoming: "Incoming",
  outgoing: "Outgoing",
  confidenceScore: "ConfidenceScore",
  contributingLayers: "ContributingLayers",
};

const TRANSACTION_FILTER_FIELDS = {
  text: new Set([
    "counterpartyName",
    "description",
    "category",
    "direction",
    "type",
    "layer",
    "status",
    "reference",
  ]),
  multiSelect: new Set(["dataSourceName"]),
  date: new Set(["forecastDate", "dueDate"]),
  number: new Set(["grossAmount", "outstandingAmount"]),
};

const BALANCE_FILTER_FIELDS = {
  text: new Set(["contributingLayers"]),
  date: new Set(["date"]),
  number: new Set([
    "openingBalance",
    "balance",
    "incoming",
    "outgoing",
    "confidenceScore",
  ]),
};

/** Distinct-value fields backing the transactions filter dropdowns. */
const TRANSACTION_DISTINCT_FIELDS = [
  "Category",
  "Direction",
  "Type",
  "Layer",
  "Status",
  "DataSourceName",
];

const TRANSACTION_EXPORT_COLUMN_MAP: Record<string, ExportColumn> = {
  counterpartyName: { name: "counterpartyname", displayName: "Counterparty" },
  description: { name: "description", displayName: "Description" },
  category: { name: "category", displayName: "Category" },
  direction: { name: "direction", displayName: "Direction" },
  type: { name: "type", displayName: "Type" },
  grossAmount: { name: "grossamount", displayName: "Gross Amount" },
  outstandingAmount: {
    name: "outstandingamount",
    displayName: "Outstanding Amount",
  },
  forecastDate: { name: "forecastdate", displayName: "Forecast Date" },
  dueDate: { name: "duedate", displayName: "Due Date" },
  layer: { name: "layer", displayName: "Layer" },
  status: { name: "status", displayName: "Status" },
  reference: { name: "reference", displayName: "Reference" },
  dataSourceName: { name: "datasourcename", displayName: "Data Source" },
};

const BALANCE_EXPORT_COLUMN_MAP: Record<string, ExportColumn> = {
  date: { name: "date", displayName: "Date" },
  openingBalance: { name: "openingbalance", displayName: "Opening Balance" },
  balance: { name: "balance", displayName: "Closing Balance" },
  incoming: { name: "incoming", displayName: "Incoming" },
  outgoing: { name: "outgoing", displayName: "Outgoing" },
  confidenceScore: { name: "confidencescore", displayName: "Confidence Score" },
  contributingLayers: {
    name: "contributinglayers",
    displayName: "Contributing Layers",
  },
};

export const TRANSACTION_DEFAULT_COLUMNS: DefaultColumn[] = [
  { key: "forecastDate", defaultVisible: true, defaultOrder: 0 },
  { key: "counterpartyName", defaultVisible: true, defaultOrder: 1 },
  { key: "description", defaultVisible: true, defaultOrder: 2 },
  { key: "category", defaultVisible: true, defaultOrder: 3 },
  { key: "direction", defaultVisible: true, defaultOrder: 4 },
  { key: "type", defaultVisible: true, defaultOrder: 5 },
  { key: "grossAmount", defaultVisible: true, defaultOrder: 6 },
  { key: "outstandingAmount", defaultVisible: true, defaultOrder: 7 },
  { key: "dueDate", defaultVisible: true, defaultOrder: 8 },
  { key: "layer", defaultVisible: true, defaultOrder: 9 },
  { key: "status", defaultVisible: true, defaultOrder: 10 },
  { key: "reference", defaultVisible: false, defaultOrder: 11 },
  { key: "dataSourceName", defaultVisible: true, defaultOrder: 12 },
];

export const BALANCE_DEFAULT_COLUMNS: DefaultColumn[] = [
  { key: "date", defaultVisible: true, defaultOrder: 0 },
  { key: "openingBalance", defaultVisible: true, defaultOrder: 1 },
  { key: "balance", defaultVisible: true, defaultOrder: 2 },
  { key: "incoming", defaultVisible: true, defaultOrder: 3 },
  { key: "outgoing", defaultVisible: true, defaultOrder: 4 },
  { key: "confidenceScore", defaultVisible: true, defaultOrder: 5 },
  { key: "contributingLayers", defaultVisible: true, defaultOrder: 6 },
];

type FormatCurrency = (value: number, precision: "full") => string;

function buildTransactionColumns(
  distinctValues: Record<string, string[]>,
  formatCurrency: FormatCurrency,
  forecastTerm: string,
): ColumnConfig<ForecastRow>[] {
  const options = (field: string) => distinctValues[field] ?? [];
  const labelledOptions = (field: string) =>
    options(field).map((v) => ({ label: formatLabel(v), value: v }));

  return [
    {
      key: "forecastDate",
      label: `${forecastTerm} Date`,
      sortable: true,
      filterable: true,
      filterConfig: { type: "date-range" },
      align: "left",
      width: 140,
      render: (row) => (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          {row.forecastDate ? formatDate(new Date(row.forecastDate)) : "—"}
        </div>
      ),
    },
    {
      key: "counterpartyName",
      label: "Counterparty",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 180,
      render: (row) => (
        <span className="text-sm">{row.counterpartyName ?? "—"}</span>
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 240,
      render: (row) => (
        <div className="truncate text-sm">{row.description ?? "—"}</div>
      ),
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: options("Category") },
      align: "left",
      width: 150,
      render: (row) => <span className="text-sm">{row.category ?? "—"}</span>,
    },
    {
      key: "direction",
      label: "Direction",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: labelledOptions("Direction") },
      align: "left",
      width: 120,
      render: (row) => (
        <span className="text-sm">{formatLabel(row.direction)}</span>
      ),
    },
    {
      key: "type",
      label: "Type",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: labelledOptions("Type") },
      align: "left",
      width: 130,
      render: (row) => <span className="text-sm">{formatLabel(row.type)}</span>,
    },
    {
      key: "grossAmount",
      label: "Gross Amount",
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 130,
      render: (row) => (
        <span
          className={cn(
            "font-semibold text-sm",
            row.direction === "inflow"
              ? "text-success-600"
              : "text-destructive",
          )}
        >
          {formatCurrency(row.grossAmount ?? 0, "full")}
        </span>
      ),
    },
    {
      key: "outstandingAmount",
      label: "Outstanding",
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 120,
      render: (row) => (
        <span className="text-sm font-semibold">
          {formatCurrency(row.outstandingAmount ?? 0, "full")}
        </span>
      ),
    },
    {
      key: "dueDate",
      label: "Due Date",
      sortable: true,
      filterable: true,
      filterConfig: { type: "date-range" },
      align: "left",
      width: 130,
      render: (row) =>
        row.dueDate ? (
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="h-3 w-3" />
            {formatDate(new Date(row.dueDate))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "layer",
      label: "Layer",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: options("Layer") },
      align: "left",
      width: 130,
      render: (row) => <span className="text-sm">{row.layer ?? "—"}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filterable: true,
      filterConfig: { type: "select", options: labelledOptions("Status") },
      align: "left",
      width: 120,
      render: (row) =>
        row.status ? (
          <Badge variant={statusBadgeVariant(row.status)}>
            {formatLabel(row.status)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "reference",
      label: "Reference",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 130,
      render: (row) => <span className="text-sm">{row.reference ?? "—"}</span>,
    },
    {
      key: "dataSourceName",
      label: "Data Source",
      sortable: true,
      filterable: true,
      filterConfig: {
        type: "multi-select",
        options: options("DataSourceName"),
      },
      align: "left",
      width: 150,
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.dataSourceName ?? "—"}
        </span>
      ),
    },
  ];
}

function buildBalanceColumns(
  formatCurrency: FormatCurrency,
  cashTerms: { in: string; out: string },
): ColumnConfig<ForecastRow>[] {
  return [
    {
      key: "date",
      label: "Date",
      sortable: true,
      filterable: true,
      filterConfig: { type: "date-range" },
      align: "left",
      width: 140,
      render: (row) => (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          {row.date ? formatDate(new Date(row.date)) : "—"}
        </div>
      ),
    },
    {
      key: "openingBalance",
      label: "Opening Balance",
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 140,
      render: (row) => (
        <span className="text-sm font-semibold">
          {formatCurrency(row.openingBalance ?? 0, "full")}
        </span>
      ),
    },
    {
      key: "balance",
      label: "Closing Balance",
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 140,
      render: (row) => (
        <span className="text-sm font-semibold">
          {formatCurrency(row.balance ?? 0, "full")}
        </span>
      ),
    },
    {
      key: "incoming",
      label: cashTerms.in,
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 130,
      render: (row) => (
        <span className="text-sm font-semibold text-success-600">
          {formatCurrency(row.incoming ?? 0, "full")}
        </span>
      ),
    },
    {
      key: "outgoing",
      label: cashTerms.out,
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 130,
      render: (row) => (
        <span className="text-sm font-semibold text-destructive">
          {formatCurrency(row.outgoing ?? 0, "full")}
        </span>
      ),
    },
    {
      key: "confidenceScore",
      label: "Confidence",
      sortable: true,
      filterable: true,
      filterConfig: { type: "number" },
      align: "right",
      width: 120,
      render: (row) =>
        row.confidenceScore != null ? (
          <span className="text-sm">{row.confidenceScore}%</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "contributingLayers",
      label: "Contributing Layers",
      sortable: true,
      filterable: true,
      filterConfig: { type: "text" },
      align: "left",
      width: 220,
      render: (row) => (
        <div className="truncate text-sm text-muted-foreground">
          {formatContributingLayers(row.contributingLayers)}
        </div>
      ),
    },
  ];
}

export interface TransactionsBalancesTableProps {
  source: TransactionsTableSource;
  /**
   * General seam for opening the table pre-filtered (e.g. navigation from a
   * dashboard/other page). Passed straight through to MaximusTableReusable as
   * the visible filter seed and applied to the live query. Pass a
   * stable/memoized object so it only re-seeds on an intentional change.
   */
  initialColumnFilters?: ColumnFilters;
  /**
   * Convenience for the same-page "View Transactions" click on the chart: when
   * set, forces Transactions view and constrains the table to that single
   * forecast day.
   */
  dateFilter?: Date | null;
  /**
   * Precomputed 7/30/90-day `forecastDate` windows for the Transactions
   * quick-filter buttons. When omitted the quick filters are not rendered.
   */
  quickFilterRanges?: QuickFilterRanges;
  /**
   * Monotonic counter that forces a live-mode refetch of rows + count while
   * preserving the current filters, sort, search and paging. Bump it whenever
   * the underlying data has changed server-side (e.g. a scenario adjustment
   * was saved or the scenario was refreshed/re-materialised).
   */
  refreshSignal?: number;
}

/**
 * Shared Transactions/Balances table used by both the base 90-day forecast
 * table and the scenario transactions table. It is a thin adapter over
 * {@link MultiViewTable}: it builds the two view definitions from
 * {@link TransactionsTableSource} and layers the quick filters and the
 * navigation filter seed on top.
 *
 * Both views are detached peers (no `resolveDrill`, `drillMember` unset), so
 * this table is not drill-capable: no view history and no Back button.
 */
export const TransactionsBalancesTable: React.FC<
  TransactionsBalancesTableProps
> = ({
  source,
  initialColumnFilters,
  dateFilter,
  quickFilterRanges,
  refreshSignal,
}) => {
  const { formatCurrency } = useFormatting();
  const { cashMovementTerms: cashTerms, getForecastTerm } = useTerminology();

  const tableRef = useRef<MultiViewTableHandle>(null);

  // A navigation seed (dashboard `initialColumnFilters` or chart `dateFilter`
  // drill-down) takes precedence over the default 90-day quick filter, so we
  // only seed the default quick filter when no navigation seed is present.
  const hasNavigationSeed = Boolean(dateFilter || initialColumnFilters);
  const defaultQuickFilterActive = Boolean(
    quickFilterRanges && !hasNavigationSeed,
  );
  const defaultQuickFilterColumnFilters = useMemo(
    () =>
      quickFilterRanges && defaultQuickFilterActive
        ? quickFilterColumnFilters(quickFilterRanges[DEFAULT_QUICK_FILTER])
        : undefined,
    // The default quick filter is a mount-time seed: recomputing it as the
    // ranges move would fight the user's current filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Which quick filter (if any) is currently applied. Defaults to 90d on mount
  // so the initial Transactions view matches the existing 90-day horizon.
  const [activeQuickFilter, setActiveQuickFilter] =
    useState<QuickFilterKey | null>(
      defaultQuickFilterActive ? DEFAULT_QUICK_FILTER : null,
    );
  const [transactionFilters, setTransactionFilters] = useState<ColumnFilters>(
    defaultQuickFilterColumnFilters ?? {},
  );

  // Navigation filter seed for the table. A same-page `dateFilter`
  // (chart "View Transactions") collapses to a single-day forecastDate range;
  // otherwise the general `initialColumnFilters` prop is used directly
  // (dashboard/other-page navigation). A new seed re-enters the Transactions
  // view with that filter applied.
  const entryColumnFilters = useMemo<
    Record<string, ColumnFilters> | undefined
  >(() => {
    if (dateFilter) {
      return {
        [TRANSACTIONS_VIEW]: {
          forecastDate: { dateFrom: dateFilter, dateTo: dateFilter },
        },
      };
    }
    if (initialColumnFilters) {
      return { [TRANSACTIONS_VIEW]: initialColumnFilters };
    }
    return undefined;
  }, [dateFilter, initialColumnFilters]);

  // A navigation seed overrides the quick filter, so no button stays teal.
  const appliedSeed = useRef(entryColumnFilters);
  useEffect(() => {
    if (appliedSeed.current === entryColumnFilters) return;
    appliedSeed.current = entryColumnFilters;
    if (!entryColumnFilters) return;
    setActiveQuickFilter(null);
    setTransactionFilters(entryColumnFilters[TRANSACTIONS_VIEW] ?? {});
  }, [entryColumnFilters]);

  const forecastTerm = getForecastTerm(true);
  const views = useMemo<MultiViewDefinition<ForecastRow>[]>(
    () => [
      defineMultiView<ForecastRow, TransactionNode>({
        key: TRANSACTIONS_VIEW,
        title: source.title,
        screenName: source.screenName,
        searchPlaceholder: "Search by description, counterparty or category...",
        columns: (distinctValues) =>
          buildTransactionColumns(distinctValues, formatCurrency, forecastTerm),
        defaultColumns: TRANSACTION_DEFAULT_COLUMNS,
        fetchList: source.fetchTransactions,
        fetchCount: source.fetchTransactionsCount,
        mapNode: (node, cursor) => ({
          rowKey: `txn-${node.id}-${cursor}`,
          id: node.id,
          forecastDate: node.forecastDate,
          dueDate: node.dueDate,
          counterpartyName: node.counterpartyName,
          description: node.description,
          category: node.category,
          direction: node.direction,
          type: node.type,
          grossAmount: node.grossAmount,
          outstandingAmount: node.outstandingAmount,
          layer: node.layer,
          status: node.status,
          reference: node.reference,
          dataSourceName: node.dataSourceName,
        }),
        sortFieldMap: TRANSACTION_SORT_FIELD_MAP,
        defaultSort: TRANSACTION_DEFAULT_SORT,
        filterFields: TRANSACTION_FILTER_FIELDS,
        distinctValueFields: TRANSACTION_DISTINCT_FIELDS,
        fetchDistinctValues: source.fetchDistinctValues,
        horizonFilter: () => source.horizonFilter(TRANSACTIONS_VIEW),
        defaultColumnFilters: defaultQuickFilterColumnFilters,
        export: {
          form: source.exportTransactionsForm,
          fileName: source.exportTransactionsFileName,
          columns: TRANSACTION_EXPORT_COLUMN_MAP,
        },
        getDemoRows: source.getDemoTransactionRows,
        getDemoOptions: () => {
          const opts = source.getDemoTransactionOptions();
          return {
            Category: opts.category,
            Direction: opts.direction,
            Type: opts.type,
            Layer: opts.layer,
            Status: opts.status,
            DataSourceName: opts.dataSource,
          };
        },
        demoSearchMatch: (row, query) =>
          [row.description, row.counterpartyName, row.category].some((value) =>
            value?.toLowerCase().includes(query),
          ),
      }),
      defineMultiView<ForecastRow, BalanceNode>({
        key: BALANCES_VIEW,
        title: source.title,
        screenName: source.screenName,
        searchPlaceholder: "Search by contributing layers...",
        columns: buildBalanceColumns(formatCurrency, cashTerms),
        defaultColumns: BALANCE_DEFAULT_COLUMNS,
        fetchList: source.fetchDataPoints,
        fetchCount: source.fetchDataPointsCount,
        mapNode: (node, cursor) => ({
          rowKey: `bal-${node.id}-${cursor}`,
          id: node.id,
          date: node.date,
          openingBalance: node.openingBalance,
          balance: node.balance,
          incoming: node.incoming,
          outgoing: node.outgoing,
          confidenceScore: node.confidenceScore,
          contributingLayers: node.contributingLayers,
        }),
        sortFieldMap: BALANCE_SORT_FIELD_MAP,
        filterFields: BALANCE_FILTER_FIELDS,
        horizonFilter: () => source.horizonFilter(BALANCES_VIEW),
        export: {
          form: source.exportBalancesForm,
          fileName: source.exportBalancesFileName,
          columns: BALANCE_EXPORT_COLUMN_MAP,
        },
        getDemoRows: source.getDemoBalanceRows,
        demoSearchMatch: (row, query) =>
          (row.contributingLayers ?? "").toLowerCase().includes(query),
      }),
    ],
    [
      source,
      formatCurrency,
      forecastTerm,
      cashTerms,
      defaultQuickFilterColumnFilters,
    ],
  );

  // Track the Transactions filters so the quick-filter buttons can merge into
  // them, and drop the active (teal) state when a manual edit/clear of the
  // forecastDate filter means the applied quick filter no longer matches.
  const handleColumnFiltersChange = useCallback(
    (filters: ColumnFilters, viewKey: string) => {
      if (viewKey !== TRANSACTIONS_VIEW) return;
      setTransactionFilters(filters);
      if (activeQuickFilter && quickFilterRanges) {
        const stillMatches = forecastDateMatchesRange(
          filters,
          quickFilterRanges[activeQuickFilter],
        );
        if (!stillMatches) setActiveQuickFilter(null);
      }
    },
    [activeQuickFilter, quickFilterRanges],
  );

  // Apply / toggle-off a 7/30/90-day quick filter. Applying merges its
  // forecastDate range into the Transactions column filters; clicking the
  // active one clears the forecastDate filter entirely and deactivates the
  // button.
  const handleQuickFilterClick = useCallback(
    (key: QuickFilterKey) => {
      if (!quickFilterRanges) return;
      let nextFilters: ColumnFilters;
      let nextActive: QuickFilterKey | null;
      if (key === activeQuickFilter) {
        const rest: ColumnFilters = { ...transactionFilters };
        delete rest.forecastDate;
        nextFilters = rest;
        nextActive = null;
      } else {
        nextFilters = {
          ...transactionFilters,
          ...quickFilterColumnFilters(quickFilterRanges[key]),
        };
        nextActive = key;
      }
      setActiveQuickFilter(nextActive);
      setTransactionFilters(nextFilters);
      tableRef.current?.applyColumnFilters(nextFilters);
    },
    [quickFilterRanges, activeQuickFilter, transactionFilters],
  );

  return (
    <MultiViewTable<ForecastRow>
      tableRef={tableRef}
      views={views}
      initialViewKey={TRANSACTIONS_VIEW}
      entryColumnFilters={entryColumnFilters}
      onColumnFiltersChange={handleColumnFiltersChange}
      horizonKey={source.horizonKey}
      refreshSignal={refreshSignal}
      errorMessage="Failed to load forecast data"
      renderHeaderActions={({ currentViewKey: activeKey, setView }) => {
        const showingTransactions = activeKey === TRANSACTIONS_VIEW;
        return (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setView(showingTransactions ? BALANCES_VIEW : TRANSACTIONS_VIEW)
              }
            >
              <ArrowLeftRight className="h-4 w-4 mr-1" />
              {showingTransactions ? "Show Balances" : "Show Transactions"}
            </Button>
            {quickFilterRanges &&
              showingTransactions &&
              QUICK_FILTER_KEYS.map((key) => {
                const isActive = activeQuickFilter === key;
                return (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-pressed={isActive}
                    className={cn(
                      isActive &&
                        "border-teal-600 bg-teal-600 text-white hover:bg-teal-700 hover:text-white",
                    )}
                    onClick={() => handleQuickFilterClick(key)}
                  >
                    {QUICK_FILTER_LABELS[key]}
                  </Button>
                );
              })}
          </>
        );
      }}
    />
  );
};
